/**
 * Gợi ý độ mạnh mật khẩu — dùng chung cho màn "Đổi mật khẩu lần đầu" (đăng nhập)
 * và tab Đổi mật khẩu của MH-40.
 *
 * Năm tiêu chí khớp đúng `matKhauDuManh` của máy chủ (packages/common
 * `xac-thuc/mat-khau.ts`): ≥ 12 ký tự, có chữ thường, chữ hoa, chữ số và ký tự
 * đặc biệt. Kiểm ở trình duyệt chỉ để báo sớm; máy chủ vẫn là nơi quyết định.
 */
import type { ReactNode } from 'react';
import { viQuanTri } from '../ngon-ngu/vi-quan-tri';

const t = viQuanTri.matKhau;

export type TieuChiMatKhau = keyof typeof t.tieuChi;

const KIEM: Record<TieuChiMatKhau, (mk: string) => boolean> = {
  doDai: (mk) => mk.length >= 12,
  chuThuong: (mk) => /[a-z]/.test(mk),
  chuHoa: (mk) => /[A-Z]/.test(mk),
  chuSo: (mk) => /[0-9]/.test(mk),
  kyTuDacBiet: (mk) => /[^A-Za-z0-9]/.test(mk),
};

/** Danh sách tiêu chí và đạt/chưa đạt, theo thứ tự hiển thị. */
export function danhGiaMatKhau(mk: string): { khoa: TieuChiMatKhau; dat: boolean }[] {
  return (Object.keys(KIEM) as TieuChiMatKhau[]).map((khoa) => ({ khoa, dat: KIEM[khoa](mk) }));
}

/** Đủ mạnh theo quy tắc máy chủ. */
export function matKhauDuManh(mk: string): boolean {
  return danhGiaMatKhau(mk).every((x) => x.dat);
}

/** Mức độ mạnh để hiển thị: số tiêu chí đạt → rỗng / yếu / trung bình / mạnh. */
export function mucDoManh(mk: string): keyof typeof t.muc {
  if (!mk) return 'rong';
  const soDat = danhGiaMatKhau(mk).filter((x) => x.dat).length;
  if (soDat === 5) return 'manh';
  if (soDat >= 3) return 'trungBinh';
  return 'yeu';
}

export function DoManhMatKhau({ matKhau, id }: { matKhau: string; id?: string }): ReactNode {
  const muc = mucDoManh(matKhau);
  return (
    <div className={`do-manh-mat-khau do-manh-mat-khau--${muc}`} id={id} data-vung="do-manh-mat-khau">
      <p className="do-manh-mat-khau__muc" aria-live="polite">
        {t.doManh}: <strong data-muc={muc}>{t.muc[muc]}</strong>
      </p>
      <ul className="do-manh-mat-khau__ds">
        {danhGiaMatKhau(matKhau).map(({ khoa, dat }) => (
          <li key={khoa} data-dat={dat ? 'co' : 'khong'}>
            <span aria-hidden="true">{dat ? '✓' : '○'}</span> {t.tieuChi[khoa]}
            <span className="chi-cho-doc-man-hinh"> — {dat ? t.datTieuChi : t.chuaDatTieuChi}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
